<template>
    <div class="my-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="article in articles" :key="article.id"
            class="relative rounded-lg overflow-hidden shadow hover:shadow-xl transition flex flex-col group">

            <!-- Card Image -->
            <div class="relative w-full h-56 cursor-pointer overflow-hidden rounded-t-lg"
                @click="$emit('edit', article)">

                <!-- Image -->
                <img v-if="article.filename && article.filename !== ''" :src="`${imageDirectory}${article.filename}`"
                    alt="Article Image"
                    class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />

                <!-- Fallback Image -->
                <div v-else class="w-full h-full flex flex-col items-center justify-center bg-gray-200 text-gray-400">
                    <svg class="w-10 h-10 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 7h18M3 12h18M3 17h18" />
                    </svg>
                    <span class="text-sm">No Image</span>
                </div>

                <!-- Top-right overlay: Highlight + Status -->
                <div class="absolute top-2 right-2 flex items-center gap-1 z-10">
                    <!-- Highlight Star -->
                    <div v-if="article.highlight"
                        class="w-5 h-5 bg-white/70 border border-yellow-300 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                        <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"
                            title="Highlighted Article">
                            <path
                                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.15c.969 0 1.371 1.24.588 1.81l-3.36 2.44a1 1 0 00-.364 1.118l1.286 3.955c.3.921-.755 1.688-1.538 1.118l-3.36-2.44a1 1 0 00-1.176 0l-3.36 2.44c-.783.57-1.838-.197-1.538-1.118l1.286-3.955a1 1 0 00-.364-1.118L2.035 9.382c-.783-.57-.38-1.81.588-1.81h4.15a1 1 0 00.95-.69l1.286-3.955z" />
                        </svg>
                    </div>

                    <!-- Status Pill -->
                    <span v-if="article.status" class="text-xs font-semibold px-2 py-0.5 rounded-full shadow"
                        :class="statusClass(article.status)">
                        {{ formatStatus(article.status) }}
                    </span>
                </div>

                <!-- Hover Overlay -->
                <div
                    class="absolute inset-0 bg-white/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 flex items-center justify-center transition duration-300 rounded-t-lg z-5">
                    <span class="text-gray-800 font-semibold bg-orange-400 text-white px-3 py-1 rounded-full">
                        View / Edit
                    </span>
                </div>

            </div>

            <!-- Card Info -->
            <div class="bg-white p-3 rounded-b-lg border-t border-gray-100 flex items-center justify-between">
                <!-- Title -->
                <h4 class="text-sm font-semibold text-gray-800 truncate">{{ article.title }}</h4>

                <!-- Timestamp -->
                <span class="text-gray-500 text-xs whitespace-nowrap">
                    {{
                        article.updatedAt
                            ? `Updated: ${new Date(article.updatedAt).toLocaleDateString()}`
                            : article.createdAt
                                ? `Created: ${new Date(article.createdAt).toLocaleDateString()}`
                                : '—'
                    }}
                </span>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    props: {
        articles: { type: Array, required: true }
    },
    emits: ["edit"],

    setup() {

        const imageDirectory = process.env.VUE_APP_GAZETTE_IMG_DIRECTORY

        const statusClass = (status) => {
            if (status === "draft") return "bg-gray-300 text-gray-800";       // light gray bg, dark text
            if (status === "published") return "bg-green-200 text-green-800"; // soft green bg, readable text
            if (status === "archived") return "bg-yellow-200 text-yellow-900"; // light yellow bg, dark text
            return "bg-gray-200 text-gray-700";                                // fallback
        };

        const formatStatus = (status) => {
            if (!status) return "";
            return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        };

        return {
            statusClass,
            formatStatus,
            imageDirectory
        };
    }
};
</script>